import { widget } from "@foretagsplatsen/widgetjs";

/**
 * `legendItemWidget` displays the legend for a single
 * series.
 *
 * @param {{}} spec - Parameter object
 * @param {string} spec.legendItemClass - CSS class for the legend item
 * @param {string} spec.activeColor - Color when the item is active
 * @param {string} spec.inactiveColor - Color when the item is inactive
 * @param {highChart} spec.chart - HighChart instance linked to the legend item
 * @param {string} [spec.title] - Title of the legend item
 * @param {function} [spec.titleRenderer] - Function returning the title to display
 * @param {{}} [spec.value] - Value of the chart linked to the legend item
 * @param {boolean} [spec.disabled] - When `true`, disable the legend item
 * @param {function} [spec.onClick] - Callback triggered when the item is clicked
 * @param {{}} my - protected fields holder
 * @return {*} legendItemWidget
 */
function legendItemWidget(spec, my) {
	spec ||= {};
	my ||= {};

	const that = widget(spec, my);

	const legendItemClass = spec.legendItemClass;
	const activeColor = spec.activeColor;
	const inactiveColor = spec.inactiveColor;
	const seriesId = spec.seriesId;
	const pairedSeriesId = spec.pairedTo;
	const title = spec.title || "-";
	const titleRenderer =
		spec.titleRenderer ||
		function () {
			return title;
		};
	let value = spec.value || "-";
	const valueRenderer =
		spec.valueRenderer ||
		function (number) {
			return number;
		};
	const disabled = spec.disabled;
	const callback = spec.onClick || function () {};
	let diff = spec.diff;
	const legendWithDiff = spec.legendWithDiff;
	const chart = spec.chart;

	that.getSeriesId = function () {
		return seriesId;
	};

	that.setValue = function (newValue) {
		value = newValue;
		that.update();
	};

	that.setDiff = function (newDiff) {
		diff = newDiff;
		that.update();
	};

	that.renderContentOn = function (html) {
		my.renderLegendItemContentOn(html);
	};

	my.renderLegendItemContentOn = function (html) {
		html.li(
			{
				class: my.getItemCssClass(),
				style: my.hasColor()
					? `background-color:${my.getColor()};`
					: "",
			},
			html.div(
				{
					class: "legend-item",
					"data-id": seriesId,
					"data-test": "legend-entry",
				},
				html.div({ class: "type" }),
				html.div({ class: "legend-item-name" }, titleRenderer(value)),
				html.div(
					{ class: "legend-item-value" },
					valueRenderer(
						value === undefined || value === null ? "-" : value,
					),
				),
				my.renderDiffOn,
			),
		).click((event) => {
			my.legendClick(event);
		});
	};

	my.renderDiffOn = function (html) {
		let valuePrefix = "";
		if (diff > 0) {
			valuePrefix = "+";
		}
		if (my.haveDiff()) {
			html.div(
				{ class: "legend-item-diff" },
				"(",
				valuePrefix,
				valueRenderer(diff),
				")",
			);
		}
	};

	my.haveDiff = function () {
		return diff !== undefined;
	};

	my.getItemCssClass = function () {
		return [
			"legend-item-container",
			`legend-item-${seriesId}`,
			legendItemClass,
			{ disabled },
			{ inactive: !disabled && !my.isActive() },
			{ "legend-with-diff": my.haveDiff() || legendWithDiff },
		];
	};

	my.hasColor = function () {
		return (
			(my.isActive() && activeColor) || (!my.isActive() && inactiveColor)
		);
	};

	my.getColor = function () {
		return my.isActive() ? activeColor : inactiveColor;
	};

	my.legendClick = function (event) {
		event.preventDefault();

		chart.toggleSeries(seriesId);
		if (pairedSeriesId) {
			chart.setSeriesVisibility(
				pairedSeriesId,
				chart.isSeriesVisible(seriesId),
			);
		}
		callback(event, my.isActive());

		that.update();
	};

	my.isActive = function () {
		return chart.isSeriesVisible(seriesId);
	};

	return that;
}

export default legendItemWidget;
